<template>
	<PopoverGroup class="hidden md:flex space-x-5 items-center">
		<Popover
			v-for="navigationElement in menuCategoriesNorce"
			:key="navigationElement.CategoryId"
		>
			<PopoverButton
				:id="`popover-button-${navigationElement.Name.toLowerCase()}`"
				v-slot="{ open }"
				class="flex items-center gap-1 text-sm uppercase text-current outline-none"
				:class="
					headerMode === 'transparent'
						? 'hover:border-white'
						: 'hover:border-secondary'
				"
			>
				<template v-if="navigationElement.Children?.length">
					<span
						class="hover:border-secondary hover:border-b-2 hover:pb-[13px] mb-[0] pb-[15px]"
						:class="
							isActiveRoute(navigationElement).value
								? 'router-link-exact-active nav-link-dropdown'
								: ''
						"
					>
						{{ navigationElement.Name }}
					</span>
					<ChevronDownIcon
						v-if="!open"
						class="h-5 w-5 flex-none text-current relative -mt-4"
						aria-hidden="true"
					/>
					<ChevronUpIcon
						v-if="open"
						class="h-5 w-5 flex-none text-current relative -mt-4"
						aria-hidden="true"
					/>
				</template>

				<NuxtLink
					v-else
					:to="
						localePath(
							`/${createCategoryLink({
								category: navigationElement
							})}`
						)
					"
					class="nav-link hover:border-secondary hover:border-b-2 hover:pb-[13px] mb-[0] pb-[15px]"
				>
					{{ navigationElement.Name }}
				</NuxtLink>
			</PopoverButton>

			<transition
				v-if="navigationElement.Children?.length"
				enter-active-class="transition ease-out duration-600"
				enter-from-class="opacity-0 translate-y-1"
				enter-to-class="opacity-100 translate-y-0"
				leave-active-class="transition ease-in duration-300"
				leave-from-class="opacity-100 translate-y-0"
				leave-to-class="opacity-0 translate-y-1"
			>
				<PopoverPanel
					v-slot="{ close }"
					class="dropdown absolute bottom-0 left-1/2 translate -translate-x-1/2 translate-y-full w-screen z-50 overflow-hidden bg-white shadow-lg border border-gray"
				>
					<div class="lg:max-w-[1280px] lg:mx-auto max-w-full">
						<div
							class="pt-6 pb-4 px-8 text-sm font-normal text-gray-super-dark hover:text-gray-dark"
						>
							<NuxtLink
								:to="
									localePath(
										`/${createCategoryLink({
											category: navigationElement
										})}`
									)
								"
								@click="close"
							>
								{{ $t("View all") }} {{ navigationElement.Name }}
							</NuxtLink>
						</div>
						<div class="container flex justify-between gap-8 pb-12 px-8">
							<div class="grid grid-cols-5 gap-8 w-full">
								<div
									v-for="childElement in navigationElement.Children"
									:key="childElement.CategoryId"
									class="group min-w-[218px] relative flex text-sm leading-6"
								>
									<div
										class="flex-auto block font-medium text-gray-900 text-sm"
									>
										<NuxtLink
											:to="
												localePath(
													`/${createCategoryLink({
														category: childElement
													})}`
												)
											"
											@click="close"
											>{{ childElement.Name }}
										</NuxtLink>
										<ul class="flex flex-col gap-3 mt-4">
											<template
												v-for="subChildElement in childElement.Children"
												:key="subChildElement.id"
											>
												<div class="flex flex-col flex-grow">
													<p
														class="text-sm font-normal text-gray-super-dark hover:text-gray-dark"
													>
														<NuxtLink
															:to="
																localePath(
																	`/${createCategoryLink({
																		category: subChildElement
																	})}`
																)
															"
															class="!border-none"
															@click="close"
														>
															{{ subChildElement.Name }}
														</NuxtLink>
													</p>
												</div>
											</template>
										</ul>
									</div>
								</div>
							</div>
							<div class="flex gap-8 justify-end">
								<NuxtLink
									:to="
										localePath(
											`/${createCategoryLink({
												category: navigationElement
											})}`
										)
									"
									@click="close"
								>
									<img
										v-if="navigationElement.ImageKey"
										:src="ImagesCdnUrl + navigationElement.ImageKey"
										class="w-80"
									/>
								</NuxtLink>
							</div>
						</div>
					</div>
				</PopoverPanel>
			</transition>
		</Popover>
	</PopoverGroup>
</template>

<script setup lang="ts">
import {
	Popover,
	PopoverButton,
	PopoverGroup,
	PopoverPanel
} from "@headlessui/vue";
import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/vue/20/solid";
import type { Category } from "@nwt/norce-pwa/dist/runtime/types/api/category/category";
import { computed } from "vue";
import { useCategory } from "~/composables/useCategory";

interface Props {
	headerMode: string;
	menuCategoriesNorce: Category[] | undefined;
}

const { headerMode, menuCategoriesNorce } = defineProps<Props>();

const localePath = useLocalePath();
const config = useRuntimeConfig();
const { createCategoryLink } = useCategory();

const { kebabCase } = useNorcePwa();
const ImagesCdnUrl = config.public.norce.cdn + "/";

const route = useRoute();

// Function to check if the route is active for the given navigation element or its children
const isActiveRoute = (navigationElement: Category) =>
	computed(() => {
		// Check if the current route matches the parent element
		if (route.path.includes(`/${kebabCase(navigationElement.Name)}`)) {
			return true;
		}

		// Check if the current route matches any child or subchild element
		return navigationElement.Children?.some((childElement: Category) => {
			if (route.path.includes(`/${kebabCase(childElement.Name)}`)) {
				return true;
			}

			return childElement.Children?.some((subChildElement: Category) =>
				route.path.includes(`/${kebabCase(subChildElement.Name)}`)
			);
		});
	});
</script>
